import { useLocation, useNavigate } from 'react-router-dom';
// @mui
import { styled, keyframes } from '@mui/material/styles';
import { BottomNavigation, BottomNavigationAction, Paper, Box } from '@mui/material';
// config
import { BOTTOM_NAV } from '../../config';
// components
import Iconify from '../../components/Iconify';

// ----------------------------------------------------------------------

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 6px rgba(66, 165, 245, 0.4); }
  50% { box-shadow: 0 0 14px rgba(66, 165, 245, 0.7); }
`;

const NAV_ITEMS = [
  { label: 'Bosh sahifa', path: '/dashboard/home', icon: 'solar:home-2-bold' },
  { label: 'Mavzular', path: '/dashboard/topics', icon: 'solar:book-bookmark-bold' },
  { label: 'Ekspertlar', path: '/dashboard/experts', icon: 'solar:users-group-rounded-bold' },
  { label: 'Reyting', path: '/dashboard/rating', icon: 'solar:cup-star-bold' },
  { label: 'Profil', path: '/dashboard/profile', icon: 'solar:user-bold' },
];

const RootStyle = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.appBar,
  background: 'linear-gradient(180deg, #0a2463 0%, #051b4a 100%)',
  borderTop: '1px solid rgba(100, 180, 255, 0.15)',
  boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.3)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    background: 'linear-gradient(90deg, transparent 0%, rgba(100, 180, 255, 0.4) 50%, transparent 100%)',
  },
}));

// ----------------------------------------------------------------------

export default function BottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const currentIndex = NAV_ITEMS.findIndex((item) => pathname.startsWith(item.path));

  return (
    <RootStyle elevation={0}>
      {/* Subtle top glow line */}
      <Box
        sx={{
          position: 'absolute',
          top: -1,
          left: '10%',
          right: '10%',
          height: 2,
          background: 'radial-gradient(ellipse at center, rgba(100, 180, 255, 0.5) 0%, transparent 70%)',
          filter: 'blur(1px)',
          zIndex: 1,
        }}
      />

      <BottomNavigation
        showLabels
        value={currentIndex === -1 ? 0 : currentIndex}
        onChange={(_, newValue) => {
          navigate(NAV_ITEMS[newValue].path);
        }}
        sx={{
          height: BOTTOM_NAV.HEIGHT,
          bgcolor: 'transparent',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 0,
            padding: '6px 4px 8px',
            color: 'rgba(255, 255, 255, 0.55)',
            transition: 'all 0.3s ease',
            position: 'relative',
            '&:hover': {
              color: 'rgba(255, 255, 255, 0.85)',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: 10,
            lineHeight: 1.1,
            mt: 0.25,
            whiteSpace: 'nowrap',
            fontWeight: 500,
            '&.Mui-selected': {
              fontSize: 11,
              fontWeight: 700,
              color: '#fff',
            },
          },
          '& .Mui-selected': {
            color: '#fff !important',
          },
          '& .MuiBottomNavigationAction-iconOnly': {
            color: 'rgba(255, 255, 255, 0.55)',
          },
        }}
      >
        {NAV_ITEMS.map((item, index) => {
          const isSelected = currentIndex === index;
          return (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    transition: 'all 0.3s ease',
                    ...(isSelected && {
                      bgcolor: 'rgba(100, 180, 255, 0.15)',
                      boxShadow: '0 0 12px rgba(66, 165, 245, 0.3)',
                      animation: `${pulseGlow} 2s ease-in-out infinite`,
                    }),
                  }}
                >
                  <Iconify
                    icon={item.icon}
                    width={22}
                    height={22}
                    sx={{
                      transition: 'all 0.3s ease',
                      filter: isSelected ? 'drop-shadow(0 0 4px rgba(100, 180, 255, 0.6))' : 'none',
                    }}
                  />
                </Box>
              }
            />
          );
        })}
      </BottomNavigation>
    </RootStyle>
  );
}
